'use strict';

import Loop54 from './Loop54';

export default {
    /**
     * If it has been fetched from the API
     */
    fetched: false,

    /**
     * If we are currently fetching it
     */
    fetching: false,

    /**
     * Any basket errors
     */
    errors: [],

    shipping: {},

    /**
     * The basket object
     */
    basket: {
        total: 0,
        sub_total: 0,
        shipping: {},
        tax_total: 0,
        has_exclusions: false,
        lines: []
    },

    /**
     * What shipping preference they have
     */
    preference: null,

    /**
     * Refresh the basket from the API
     */
    refresh() {
        this.fetching = true;

        return new Promise((resolve, reject) => {
            axios({
                method: 'GET',
                url: '/api/basket',
                headers: {
                    'Accept': 'application/json'
                }
            })
            .then(response => {
                this.basket = response.data;
                this.basket.shipping = this.shipping;
                this.fetched = true;
                this.fetching = false;
                resolve(response.data);
            })
            .catch(error => {
                reject(error);
            });
        });
    },

    addMeta(basketId, key, value) {
        return new Promise((resolve, reject) => {
            axios({
                method: 'POST',
                url: '/api/baskets/meta',
                data: {
                    id: basketId,
                    key: key,
                    value: value,
                },
                headers: {
                    'Accept': 'application/json'
                }
            })
            .then(response => {
                this.basket = response.data;
                this.basket.shipping = this.shipping;
                this.fetched = true;
                this.fetching = false;
                resolve(response.data);
            })
            .catch(error => {
                reject(error);
            });
        });
    },

    /**
     * Gets the basket.
     */
    getBasket() {
        if (!this.fetched) {
            this.fetched = true;
            this.refresh();
        }
        return this.basket;
    },

    /**
     * Clears basket errors
     */
    clearErrors() {
        this.errors = [];
    },

    additionalDelivery() {
        let total = 0;
        _.each(this.basket.lines, line => {
            total += line.additional_delivery ? line.additional_delivery : 0;
        });
        return total;
    },

    /**
     * Sets the basket lines
     * @param {Array} lines
     */
    setLines(lines) {

        this.clearErrors();

        let newLines = _.map(lines, line => {
            if (line.variant) {
                return {
                    id: line.variant.id,
                    quantity: line.quantity && line.quantity != 0 ? line.quantity : 1,
                    meta: line.meta ? line.meta : [],
                };
            }
            return line;
        });

        return new Promise((resolve, reject) => {
            axios({
                method: 'post',
                url: '/api/basket',
                headers: {
                    'Accept': 'application/json'
                },
                data: {
                    variants: newLines
                }
            })
                .then(response => {
                    this.refresh();
                    resolve(response.data);
                })
                .catch(error => {
                    this.errors = error.response.data;
                    reject(error);
                });
        });
    },

    /**
     * The incoming lines with the current lines
     * @param {Array} lines
     */
    mergeLines(lines) {

        // Get rid of any duplicates, favouring the new.
        return this.refresh().then(response => {

            let newIds = _.map(lines, line => {return line.id});

            let existing = _.map(this.basket.lines, line => {
                return {
                    id: line.variant.id,
                    quantity: line.quantity
                }
            });

            existing = _.reject(existing, line => {
                return newIds.includes(line.id);
            });

            return this.setLines(lines.concat(existing));
        });
    },

    /**
     * Add a variant to the basket
     * @param {string} variant
     * @param {number} quantity
     */
    add(variant, quantity, meta) {

        this.clearErrors();

        // Make sure we always have the latest basket.
        return this.refresh().then(response => {
            let lines = _.map(this.basket.lines, line => {
                return {
                    id: line.variant.id,
                    quantity: line.quantity,
                    meta: line.meta,
                }
            });

            // If the product is already in the basket, add to it
            let addNew = true;

            _.each(lines, line => {
                if (line.id == variant) {
                    addNew = false;
                    line.quantity += parseInt(quantity);
                    line.meta = meta;
                }
            });

            if (addNew) {
                lines.push({
                    id: variant,
                    quantity: quantity,
                    meta: meta ? meta : null,
                });
            }

            return new Promise((resolve, reject) => {
                axios({
                    method: 'post',
                    url: '/api/basket',
                    headers: {
                        'Accept': 'application/json'
                    },
                    data: {
                        variants: lines
                    }
                })
                    .then(response => {
                        const basketResponse = response.data;
                        this.refresh();
                        Loop54.trigger('AddToCart', variant, quantity).then(response => {
                            resolve(basketResponse);
                        }).catch(errors => {
                            resolve(basketResponse);
                        });
                    })
                    .catch(error => {
                        this.errors = error.response.data;
                        reject(error);
                    });
            });
        });
    },

    /**
     * Saves the current basket in the session
     * @param {string} name The name of the basket
     */
    save(name) {
        if (!name) {
            name = 'Saved basket';
        }

        return new Promise((resolve, reject) => {
            axios({
                method: 'POST',
                url: '/api/baskets/save',
                headers: {
                    'Accept': 'application/json'
                },
                data: {
                    name: name
                }
            })
            .then(response => {
                this.attachUser(response.data.id).then(response => {
                    resolve(response.data);
                });
            })
            .catch(error => {
                reject(error);
            });
        });
    },

    setEstimatedShipping(shipping) {
        this.shipping = shipping;
        this.refresh();
        return this;
    },

    /**
     * Set the delivery preference
     * @param {string} preference
     */
    setPreference(preference) {
        this.preference = preference;
        return this;
    },

    /**
     * Reset the basket to it's default state
     */
    reset() {
        this.fetched = false;
        this.fetching = false;
        this.errors = [];
        this.preference = null;
        this.basket = {
            total: 0,
            sub_total: 0,
            tax_total: 0,
            lines: []
        };
    },

    attachUser(basketId) {
        return new Promise((resolve, reject) => {
            axios({
                method: 'POST',
                url: '/api/baskets/attach',
                headers: {
                    'Accept': 'application/json'
                },
                data: {
                    basket_id: basketId
                }
            })
            .then(response => {
                resolve(response.data);
            })
            .catch(error => {
                reject(error);
            });
        });
    }
}



// WEBPACK FOOTER //
// ./resources/assets/js/Services/Basket.js